import React from 'react';
import Modal from 'react-modal';
import AbstractComponent from '../../../common/components/AbstractComponent';
import * as PropTypes from 'prop-types';
import { ElementsConsumer, CardElement } from '@stripe/react-stripe-js';
import {
  SvgCheckIcon,
  SvgErrorIcon,
  SvgVisaCardIcon,
  SvgMasterCardIcon,
  SvgAmericanExpressCardIcon,
  SvgDinersClubCardIcon,
  SvgDiscoverCardIcon,
  SvgJCBCardIcon,
} from '../../../index';
import { MnButton } from '../../../common/components/index';
import styles from '../../../../stylesheets/user/_var.scss';

const CARD_ELEMENT_OPTIONS = {
  hidePostalCode: true,
  style: {
    base: {
      fontSize: '16px',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      color: styles.BlackColor,
      '::placeholder': {
        color: styles.GrayColor40,
      },
    },
    invalid: {
      color: styles.ErrorColor,
      iconColor: styles.ErrorColor,
    },
  },
};

class CardSetupForm extends AbstractComponent {
  constructor (props) {
    super(props);
    this.state = {
      isAlert: false,
      clientSecret: null,
      paymentMethodId: null,
      errorMessage: null,
      cardElementInput: null,
    };
  }

  handleReady = (event) => this.setState({ cardElementInput: event });

  handleChange = (event) => {
    const key = event?.error?.code ? `CardSetupForm.${event?.error?.code}` : undefined;
    if (key && this.props.t(key) === key) {
      this.setState({ errorMessage: event?.error?.message });
    } else {
      this.setState({ errorMessage: key });
    }
  }

  handleSubmit = async (event) => {
    event.preventDefault();

    // すでにカードが登録済みの場合はなにもしない
    if (this.props.isRegister) { return; }

    const { stripe, elements } = this.props;
    const clientSecret = this.state.clientSecret || this.props.clientSecret;

    if (!stripe || !elements) {
      return;
    }

    const result = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: elements.getElement(CardElement),
      },
    });

    if (result.error) {
      const { message } = result.error;
      this.setState({ errorMessage: message });
      this.props.onUnregister();
    } else {
      const paymentMethodId = result.setupIntent.payment_method;
      this.setState({ errorMessage: null, paymentMethodId: paymentMethodId });
      this.props.onRegister();
      this.props.onToast(this.props.t('CardSetupForm.registerd'));

      // 登録後、CardElement 全体を非活性色に変更
      // 要素自体に className を指定すると一時的にデザインが崩れ、option だと枠いっぱいの背景色を変更できないため、このような実装
      this.state.cardElementInput._parent.style.backgroundColor = styles.GrayColor30;
    }
  };

  handleReset = async () => {
    return new Promise(resolve => {
      const { paymentMethodId } = this.state;
      this.fetch(this.props.clientSecretResetPath, {
        methods: 'POST',
        body: { payment_method_id: paymentMethodId },
      }).then(body => {
        this.setState({
          clientSecret: body.client_secret,
          errorMessage: null,
          paymentMethodId: null,
        });
        this.props.onUnregister();
        this.props.onToast(this.props.t('CardSetupForm.unregisterd'));

        const { cardElementInput } = this.state;
        cardElementInput.clear();
        cardElementInput.focus();
        // 登録解除後、CardElement 全体を通常色に変更
        // 要素自体に className を指定すると一時的にデザインが崩れ、option だと枠いっぱいの背景色を変更できないため、このような実装
        cardElementInput._parent.style.backgroundColor = styles.GrayColor30;
      }).catch(_ => {
        // clientSecretの再発行処理で失敗すると Customerとの紐付けができなくなるため再ロードさせる
        this.setState({ isAlert: true });
      }).finally(() => {
        resolve();
      });
    });
  }

  render () {
    const { isRegister } = this.props;
    const errorMessage = this.state.errorMessage || this.props.errorMessage;
    console.log(this.props);

    return (
      <>
        <form onSubmit={this.handleSubmit}>
          <div className="d-fx jc-sb">
            <div className="p-interview--content--icons">
              { SvgVisaCardIcon }
              { SvgMasterCardIcon }
              { SvgAmericanExpressCardIcon }
              { SvgDiscoverCardIcon }
              { SvgDinersClubCardIcon }
              { SvgJCBCardIcon }
            </div>
            { isRegister && <div className="ta-r lh-nm d-fx ai-c">{ SvgCheckIcon }{this.props.t('CardSetupForm.credit_card_registerd')}</div> }
          </div>

          <CardElement
            options={ { ...CARD_ELEMENT_OPTIONS, disabled: isRegister } }
            onReady={this.handleReady}
            onChange={this.handleChange}
          />

          { errorMessage && <p className="c-mn-form-caption c-mn-form-caption--error mb-md">{ SvgErrorIcon }{this.props.t(errorMessage)}</p> }

          { isRegister ? (
            <MnButton class="c-mn-btn--third p-interview--content--btn" onClick={this.handleReset} disabled={!this.props.stripe}>
              <span>{this.props.t('CardSetupForm.credit_card_unregister')}</span>
            </MnButton>
          ) : (
            <button className="c-mn-btn--third p-interview--content--btn" disabled={!this.props.stripe}>
              <span>{this.props.t('CardSetupForm.credit_card_register')}</span>
            </button>
          ) }
        </form>

        <Modal isOpen={this.state.isAlert} className="c-mn-modal-dialog is-active" overlayClassName="c-mn-modal is-active--dialog">
          <p className="c-mn-modal-dialog__title">{this.props.t('CardSetupForm.fatal_error')}</p>
          <div className="c-mn-modal-dialog__txt">{this.props.t('CardSetupForm.fatal_error_annotation')}</div>
          <div className="c-mn-modal-dialog__btn-wrap">
            <a className="c-mn-btn c-mn-btn--basic-s c-mn-btn--first c-mn-btn--compact js-modal-dialog02-close" onClick={ () => location.reload() }>
              <span>OK</span>
            </a>
          </div>
        </Modal>
      </>
    );
  }
}

export default function InjectedCardSetupForm (props) {
  return (
    <ElementsConsumer>
      {({ stripe, elements }) => (
        <CardSetupForm
          stripe={stripe}
          elements={elements}
          clientSecret={props.clientSecret}
          clientSecretResetPath={props.clientSecretResetPath}
          isRegister={props.isRegister}
          onRegister={props.onRegister}
          onUnregister={props.onUnregister}
          onToast={props.onToast}
          errorMessage={props.errorMessage}
          t={props.t}
        />
      )}
    </ElementsConsumer>
  );
}

CardSetupForm.propTypes = {
  stripe: PropTypes.object,
  elements: PropTypes.object,
  clientSecret: PropTypes.string,
  clientSecretResetPath: PropTypes.string,
  isRegister: PropTypes.bool,
  onRegister: PropTypes.func,
  onUnregister: PropTypes.func,
  onToast: PropTypes.func,
  errorMessage: PropTypes.string,
  t: PropTypes.func,
};

InjectedCardSetupForm.propTypes = { ...CardSetupForm.propTypes };
